import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import {
    Loader2,
    Search,
    Filter,
    X,
    Download,
    ArrowLeft,
    ArrowRight,
    ChevronsLeft,
    ChevronsRight,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "@/lib/api";
import { toDdMmYyyy } from "@/lib/dateFormat";

const ANY = "__any__";
const PAGE_SIZES = [10, 25, 50, 100];

const COLUMN_DEFS = [
    ["name", "Name", true],
    ["phone", "Phone", true],
    ["email", "Email", true],
    ["college_name", "College", true],
    ["course", "Course", false],
    ["branch", "Branch", false],
    ["role", "Role", true],
    ["schedule_date", "Schedule Date", true],
    ["schedule_time", "Schedule Time", true],
    ["otp", "OTP", false],
    ["attendance_status", "Attendance", true],
    ["otp_verification_status", "OTP Verification", false],
    ["attendance_verified_date", "Verified Date", false],
    ["attendance_verified_time", "Verified Time", false],
    ["whatsapp_status", "WhatsApp", true],
    ["send_attempts", "Send Attempts", false],
    ["registration_type", "Registration Type", true],
    ["active_status", "Active", true],
    ["created_date", "Created", false],
    ["updated_date", "Updated", false],
];

const SORTABLE_FIELDS = new Set([
    "name",
    "email",
    "phone",
    "college_name",
    "role",
    "schedule_date",
    "schedule_time_24h",
    "attendance_status",
    "otp_verification_status",
    "registration_type",
    "active_status",
    "created_at",
    "updated_at",
]);

// UI column key -> backend sort field
const SORT_MAP = {
    name: "name",
    phone: "phone",
    email: "email",
    college_name: "college_name",
    role: "role",
    schedule_date: "schedule_date",
    schedule_time: "schedule_time_24h",
    attendance_status: "attendance_status",
    otp_verification_status: "otp_verification_status",
    registration_type: "registration_type",
    active_status: "active_status",
    created_date: "created_at",
    updated_date: "updated_at",
};

const emptyFilters = () => ({
    search: "",
    attendance_status: ANY,
    schedule_date: "",
    schedule_time: "",
    college_name: "",
    role: "",
    course: "",
    branch: "",
    otp_verification_status: ANY,
    whatsapp_status: ANY,
    registration_type: ANY,
    active_status: ANY,
    created_from: "",
    created_to: "",
    updated_from: "",
    updated_to: "",
});

export default function AttendanceReport() {
    const [filters, setFilters] = useState(emptyFilters());
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [sortBy, setSortBy] = useState("created_date");
    const [sortDir, setSortDir] = useState("desc");

    const [facets, setFacets] = useState({ roles: [], colleges: [] });
    const [publicColleges, setPublicColleges] = useState([]);
    const [publicRoles, setPublicRoles] = useState([]);

    const [exportOpen, setExportOpen] = useState(false);
    const [exportCols, setExportCols] = useState(() =>
        COLUMN_DEFS.map(([k]) => k),
    );
    const [exportFormat, setExportFormat] = useState("csv");
    const [downloading, setDownloading] = useState(false);

    const buildParams = useCallback(() => {
        const params = {
            paginated: true,
            limit: pageSize,
            skip: (page - 1) * pageSize,
            sort_by: SORT_MAP[sortBy] || "created_at",
            sort_dir: sortDir,
        };
        if (filters.search.trim()) params.search = filters.search.trim();
        if (filters.attendance_status !== ANY)
            params.attendance_status = filters.attendance_status;
        if (filters.otp_verification_status !== ANY)
            params.otp_verification_status = filters.otp_verification_status;
        if (filters.whatsapp_status !== ANY)
            params.whatsapp_status = filters.whatsapp_status;
        if (filters.registration_type !== ANY)
            params.registration_type = filters.registration_type;
        if (filters.active_status !== ANY)
            params.active_status = filters.active_status;
        if (filters.schedule_date)
            params.schedule_date = toDdMmYyyy(filters.schedule_date);
        if (filters.schedule_time) params.schedule_time = filters.schedule_time;
        if (filters.college_name.trim())
            params.college = filters.college_name.trim();
        if (filters.role.trim()) params.role = filters.role.trim();
        if (filters.course.trim()) params.course = filters.course.trim();
        if (filters.branch.trim()) params.branch = filters.branch.trim();
        if (filters.created_from)
            params.created_from = `${filters.created_from}T00:00:00`;
        if (filters.created_to)
            params.created_to = `${filters.created_to}T23:59:59`;
        if (filters.updated_from)
            params.updated_from = `${filters.updated_from}T00:00:00`;
        if (filters.updated_to)
            params.updated_to = `${filters.updated_to}T23:59:59`;
        return params;
    }, [filters, page, pageSize, sortBy, sortDir]);

    const fetchPage = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/candidates", { params: buildParams() });
            setRows(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    }, [buildParams]);

    useEffect(() => {
        fetchPage();
    }, [fetchPage]);

    useEffect(() => {
        api.get("/candidates/facets")
            .then(({ data }) =>
                setFacets({
                    roles: data.roles || [],
                    colleges: data.colleges || [],
                }),
            )
            .catch(() => {});
        api.get("/public/colleges")
            .then(({ data }) => setPublicColleges(data || []))
            .catch(() => {});
        api.get("/public/job-roles")
            .then(({ data }) => setPublicRoles(data || []))
            .catch(() => {});
    }, []);

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    const setSort = (key) => {
        if (!SORTABLE_FIELDS.has(SORT_MAP[key])) return;
        if (sortBy === key) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(key);
            setSortDir("asc");
        }
        setPage(1);
    };

    const sortIcon = (key) => {
        if (!SORTABLE_FIELDS.has(SORT_MAP[key]))
            return <span className="opacity-0"><ArrowUpDown size={11} /></span>;
        if (sortBy !== key) return <ArrowUpDown size={11} className="opacity-40" />;
        return sortDir === "asc" ? (
            <ArrowUp size={11} />
        ) : (
            <ArrowDown size={11} />
        );
    };

    const doDownload = async () => {
        if (exportCols.length === 0) {
            toast.error("Pick at least one column.");
            return;
        }
        setDownloading(true);
        try {
            const params = buildParams();
            const filterBody = { ...params };
            // The export endpoint expects filters as an object, not query params.
            delete filterBody.paginated;
            delete filterBody.limit;
            delete filterBody.skip;
            delete filterBody.sort_by;
            delete filterBody.sort_dir;
            // Rename `college` -> `college_name` for export API.
            if (filterBody.college) {
                filterBody.college_name = filterBody.college;
                delete filterBody.college;
            }
            const resp = await api.post(
                "/candidates/export",
                {
                    filters: filterBody,
                    columns: exportCols,
                    format: exportFormat,
                    sort_by: SORT_MAP[sortBy] || "created_at",
                    sort_dir: sortDir,
                },
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(new Blob([resp.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download =
                exportFormat === "xlsx" ? "attendance.xlsx" : "attendance.csv";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success("Export downloaded");
            setExportOpen(false);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setDownloading(false);
        }
    };

    const setF = (patch) => {
        setFilters((f) => ({ ...f, ...patch }));
        setPage(1);
    };

    const attendancePill = (v) =>
        v === "Attended" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                Attended
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-[#F7F5F0] text-[#0F1F38]/70 border border-[#0F1F38]/15">
                Not Attended
            </span>
        );

    const activePill = (v) =>
        v === "Inactive" ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700 border border-red-200">
                Inactive
            </span>
        ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                Active
            </span>
        );

    return (
        <div data-testid="attendance-report-page">
            <AdminPageHeader
                eyebrow="Attendance"
                title="Attendance Report"
                description="Browse filtered attendance directly, then export exactly what you see — same filters, same sort order."
                actions={
                    <button
                        data-testid="btn-open-export"
                        onClick={() => setExportOpen(true)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                    >
                        <Download size={14} strokeWidth={1.5} />
                        Export
                    </button>
                }
            />

            <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 space-y-4">
                {/* Filter bar */}
                <div
                    data-testid="report-filter-bar"
                    className="bg-white border border-[#0F1F38]/10 p-4 md:p-5 grid grid-cols-1 md:grid-cols-6 gap-3"
                >
                    <div className="md:col-span-2">
                        <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                            Search
                        </Label>
                        <div className="relative">
                            <Search
                                size={14}
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0F1F38]/40"
                            />
                            <Input
                                data-testid="rep-search"
                                value={filters.search}
                                onChange={(e) => setF({ search: e.target.value })}
                                className="pl-9"
                                placeholder="Name, email, phone…"
                            />
                        </div>
                    </div>
                    <FSelect
                        label="Attendance"
                        testid="rep-attendance"
                        value={filters.attendance_status}
                        onValue={(v) => setF({ attendance_status: v })}
                        options={[["Attended", "Attended"], ["Not Attended", "Not Attended"]]}
                    />
                    <FSelect
                        label="OTP Verification"
                        testid="rep-otp"
                        value={filters.otp_verification_status}
                        onValue={(v) => setF({ otp_verification_status: v })}
                        options={[["Verified", "Verified"], ["Pending", "Pending"]]}
                    />
                    <FSelect
                        label="WhatsApp"
                        testid="rep-wa"
                        value={filters.whatsapp_status}
                        onValue={(v) => setF({ whatsapp_status: v })}
                        options={[["sent", "Sent"], ["not_sent", "Not Sent"]]}
                    />
                    <FSelect
                        label="Registration Type"
                        testid="rep-regtype"
                        value={filters.registration_type}
                        onValue={(v) => setF({ registration_type: v })}
                        options={[
                            ["Excel Upload", "Excel Upload"],
                            ["Self Registration", "Self Registration"],
                        ]}
                    />

                    <FInput
                        label="Schedule Date"
                        testid="rep-date"
                        type="date"
                        value={filters.schedule_date}
                        onValue={(v) => setF({ schedule_date: v })}
                    />
                    <FInput
                        label="Schedule Time"
                        testid="rep-time"
                        placeholder="e.g. 10:30 AM"
                        value={filters.schedule_time}
                        onValue={(v) => setF({ schedule_time: v })}
                    />
                    <FAutocomplete
                        label="College Name"
                        testid="rep-college"
                        value={filters.college_name}
                        onValue={(v) => setF({ college_name: v })}
                        options={[
                            ...publicColleges.map((c) => c.name),
                            ...facets.colleges.filter(
                                (c) => !publicColleges.some((p) => p.name === c),
                            ),
                        ]}
                    />
                    <FAutocomplete
                        label="Job Role"
                        testid="rep-role"
                        value={filters.role}
                        onValue={(v) => setF({ role: v })}
                        options={[
                            ...publicRoles.map((r) => r.name),
                            ...facets.roles.filter(
                                (r) => !publicRoles.some((p) => p.name === r),
                            ),
                        ]}
                    />
                    <FInput
                        label="Course"
                        testid="rep-course"
                        value={filters.course}
                        onValue={(v) => setF({ course: v })}
                    />
                    <FInput
                        label="Branch"
                        testid="rep-branch"
                        value={filters.branch}
                        onValue={(v) => setF({ branch: v })}
                    />
                    <FSelect
                        label="Active"
                        testid="rep-active"
                        value={filters.active_status}
                        onValue={(v) => setF({ active_status: v })}
                        options={[
                            ["Active", "Active"],
                            ["Inactive", "Inactive"],
                        ]}
                    />
                    <FInput
                        label="Created From"
                        testid="rep-created-from"
                        type="date"
                        value={filters.created_from}
                        onValue={(v) => setF({ created_from: v })}
                    />
                    <FInput
                        label="Created To"
                        testid="rep-created-to"
                        type="date"
                        value={filters.created_to}
                        onValue={(v) => setF({ created_to: v })}
                    />
                    <FInput
                        label="Updated From"
                        testid="rep-updated-from"
                        type="date"
                        value={filters.updated_from}
                        onValue={(v) => setF({ updated_from: v })}
                    />
                    <FInput
                        label="Updated To"
                        testid="rep-updated-to"
                        type="date"
                        value={filters.updated_to}
                        onValue={(v) => setF({ updated_to: v })}
                    />

                    <div className="md:col-span-6 flex items-center justify-between pt-2 border-t border-[#0F1F38]/5">
                        <div className="text-xs text-[#4B5563]">
                            <strong data-testid="record-count">{total}</strong> matching
                            record{total === 1 ? "" : "s"}
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="btn-clear-filters"
                                onClick={() => {
                                    setFilters(emptyFilters());
                                    setPage(1);
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[#0F1F38]/15"
                            >
                                <X size={12} /> Reset
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-[#0F1F38]/10">
                    {loading ? (
                        <div className="p-16 text-center text-[#4B5563]">
                            <Loader2
                                size={18}
                                className="animate-spin inline mr-2"
                            />
                            Loading…
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-16 text-center text-[#4B5563]">
                            No matching candidates.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="report-table">
                                <TableHeader>
                                    <TableRow>
                                        {[
                                            ["name", "Name"],
                                            ["phone", "Phone"],
                                            ["email", "Email"],
                                            ["college_name", "College"],
                                            ["role", "Role"],
                                            ["schedule_date", "Schedule"],
                                            ["attendance_status", "Attendance"],
                                            ["otp_verification_status", "OTP"],
                                            ["registration_type", "Registration"],
                                            ["active_status", "Active"],
                                            ["created_date", "Created"],
                                        ].map(([k, label]) => (
                                            <TableHead
                                                key={k}
                                                data-testid={`th-${k}`}
                                                onClick={() => setSort(k)}
                                                className={`select-none ${
                                                    SORTABLE_FIELDS.has(
                                                        SORT_MAP[k],
                                                    )
                                                        ? "cursor-pointer hover:bg-[#0F1F38]/5"
                                                        : ""
                                                }`}
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    {label}
                                                    {sortIcon(k)}
                                                </span>
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((c) => (
                                        <TableRow
                                            key={c.id}
                                            data-testid={`report-row-${c.id}`}
                                        >
                                            <TableCell className="font-medium">
                                                {c.name}
                                            </TableCell>
                                            <TableCell className="tabular-nums">
                                                {c.phone}
                                            </TableCell>
                                            <TableCell className="text-[#4B5563]">
                                                {c.email}
                                            </TableCell>
                                            <TableCell>
                                                {c.college_name || "—"}
                                            </TableCell>
                                            <TableCell>{c.role}</TableCell>
                                            <TableCell className="text-xs">
                                                {c.schedule_date}
                                                <div className="text-[10px] text-[#0F1F38]/50">
                                                    {c.schedule_time}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {attendancePill(
                                                    c.attendance_status,
                                                )}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {c.otp_verification_status}
                                            </TableCell>
                                            <TableCell className="text-xs">
                                                {c.registration_type}
                                            </TableCell>
                                            <TableCell>
                                                {activePill(c.active_status)}
                                            </TableCell>
                                            <TableCell className="text-[10px] text-[#0F1F38]/60">
                                                {c.created_at?.slice(0, 10) || "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div
                    data-testid="pagination-bar"
                    className="bg-white border border-[#0F1F38]/10 px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                >
                    <div className="flex items-center gap-3 text-xs text-[#4B5563]">
                        <span>Rows per page</span>
                        <Select
                            value={String(pageSize)}
                            onValueChange={(v) => {
                                setPageSize(parseInt(v, 10));
                                setPage(1);
                            }}
                        >
                            <SelectTrigger
                                data-testid="page-size"
                                className="w-20"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {PAGE_SIZES.map((n) => (
                                    <SelectItem key={n} value={String(n)}>
                                        {n}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <span>
                            Showing{" "}
                            <strong>
                                {rows.length === 0
                                    ? 0
                                    : (page - 1) * pageSize + 1}
                                –
                                {(page - 1) * pageSize + rows.length}
                            </strong>{" "}
                            of <strong>{total}</strong>
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <PageBtn
                            testid="pg-first"
                            onClick={() => setPage(1)}
                            disabled={page === 1}
                        >
                            <ChevronsLeft size={14} />
                        </PageBtn>
                        <PageBtn
                            testid="pg-prev"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page === 1}
                        >
                            <ArrowLeft size={14} />
                        </PageBtn>
                        <div className="px-3 text-xs text-[#0F1F38]/70">
                            Page{" "}
                            <strong data-testid="current-page">{page}</strong> of{" "}
                            <strong>{totalPages}</strong>
                        </div>
                        <PageBtn
                            testid="pg-next"
                            onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                            }
                            disabled={page >= totalPages}
                        >
                            <ArrowRight size={14} />
                        </PageBtn>
                        <PageBtn
                            testid="pg-last"
                            onClick={() => setPage(totalPages)}
                            disabled={page >= totalPages}
                        >
                            <ChevronsRight size={14} />
                        </PageBtn>
                    </div>
                </div>
            </div>

            {/* Export dialog */}
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
                <DialogContent
                    data-testid="export-dialog"
                    className="max-w-lg"
                >
                    <DialogHeader>
                        <DialogTitle>Export current view</DialogTitle>
                    </DialogHeader>
                    <div className="text-xs text-[#4B5563]">
                        Export respects all applied filters and the current sort
                        order (<strong>{sortBy}</strong> {sortDir}). {total}{" "}
                        record{total === 1 ? "" : "s"} match.
                    </div>
                    <div className="grid grid-cols-2 gap-2 my-4">
                        {["csv", "xlsx"].map((f) => (
                            <button
                                key={f}
                                data-testid={`fmt-${f}`}
                                onClick={() => setExportFormat(f)}
                                className={`py-2 text-sm font-medium border ${
                                    exportFormat === f
                                        ? "bg-[#0F1F38] text-[#F7F5F0] border-[#0F1F38]"
                                        : "border-[#0F1F38]/20 hover:border-[#0F1F38]"
                                }`}
                            >
                                {f.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-label text-[#0F1F38]/60 mb-2 flex items-center justify-between">
                            Columns ({exportCols.length}/{COLUMN_DEFS.length})
                            <div className="flex gap-2 normal-case tracking-normal">
                                <button
                                    onClick={() =>
                                        setExportCols(
                                            COLUMN_DEFS.map(([k]) => k),
                                        )
                                    }
                                    className="text-[11px] underline"
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setExportCols([])}
                                    className="text-[11px] underline"
                                >
                                    None
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-1 max-h-56 overflow-auto border border-[#0F1F38]/10 p-2">
                            {COLUMN_DEFS.map(([k, label]) => (
                                <label
                                    key={k}
                                    className="flex items-center gap-2 px-2 py-1 text-xs hover:bg-[#F7F5F0]/60"
                                >
                                    <Checkbox
                                        data-testid={`exp-col-${k}`}
                                        checked={exportCols.includes(k)}
                                        onCheckedChange={(v) => {
                                            if (v)
                                                setExportCols([
                                                    ...exportCols,
                                                    k,
                                                ]);
                                            else
                                                setExportCols(
                                                    exportCols.filter(
                                                        (x) => x !== k,
                                                    ),
                                                );
                                        }}
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={() => setExportOpen(false)}
                            disabled={downloading}
                            className="px-4 py-2 text-sm border border-[#0F1F38]/20"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="btn-confirm-export"
                            onClick={doDownload}
                            disabled={downloading || exportCols.length === 0}
                            className="px-4 py-2 text-sm bg-emerald-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                        >
                            {downloading ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Download size={14} />
                            )}
                            Download
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function FSelect({ label, testid, value, onValue, options }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Select value={value} onValueChange={onValue}>
                <SelectTrigger data-testid={testid}>
                    <SelectValue placeholder="Any" />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value={ANY}>Any</SelectItem>
                    {options.map(([v, lab]) => (
                        <SelectItem key={v} value={v}>
                            {lab}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    );
}

function FInput({ label, testid, value, onValue, type = "text", placeholder }) {
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <Input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onValue(e.target.value)}
                placeholder={placeholder}
            />
        </div>
    );
}

function FAutocomplete({ label, testid, value, onValue, options }) {
    const listId = `${testid}-list`;
    return (
        <div>
            <Label className="text-[10px] uppercase tracking-label text-[#0F1F38]/60">
                {label}
            </Label>
            <input
                data-testid={testid}
                list={listId}
                value={value}
                onChange={(e) => onValue(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                placeholder="Any"
            />
            <datalist id={listId}>
                {options.map((o) => (
                    <option key={o} value={o} />
                ))}
            </datalist>
        </div>
    );
}

function PageBtn({ testid, onClick, disabled, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            disabled={disabled}
            className="p-1.5 border border-[#0F1F38]/15 hover:border-[#0F1F38] disabled:opacity-40 disabled:cursor-not-allowed"
        >
            {children}
        </button>
    );
}
